import TableListAssigned from "@/shared/components/organisms/TableListAssigned";
import { useGetAllAssignedQuery } from "./api/assigne.api";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/auth/auth.slice";
import { useSupportStaffQuery } from "@/app/users/users.api";
import IncidenceModal from "@/shared/components/organisms/IncidenceModal";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { Select } from "@/shared/components/organisms/Select";
import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import { useFormik } from "formik";
import { useFetchGymsMutation } from "@/app/gyms/gyms.api";
import { useBranchsGymQuery } from "@/app/branchs/branchs.api";
import { useNavigate } from "react-router-dom";

interface GeneralOption {
  id: number | string;
  name: string;
}

interface FormikInitialValuesProps {
  gym: GeneralOption;
  branch: GeneralOption;
  personal: GeneralOption;
  lastVisit: GeneralOption & { value: string };
}
const initialValues: FormikInitialValuesProps = {
  branch: { id: "", name: "" },
  gym: { id: "", name: "" },
  personal: { id: "", name: "" },
  lastVisit: { id: "", name: "", value: "" },
};

interface LatestVisitOptionsProp extends GeneralOption {
  value: string;
}

const latestVisitOptions: LatestVisitOptionsProp[] = [
  {
    id: 1,
    name: ">5",
    value: "5",
  },
  {
    id: 2,
    name: ">10",
    value: "10",
  },
  {
    id: 3,
    name: ">15",
    value: "15",
  },
  {
    id: 4,
    name: ">20",
    value: "20",
  },
];

// Define una interfaz para la estructura de tus datos de usuario
interface UserData {
  id: number;
  name: string;
  email: string;
  // Asegúrate de que todas las propiedades que uses para filtrar
  // y las anidadas (como branch.gym.gym o branch.branch) estén definidas aquí.
  branch?: {
    id: number;
    branch: string;
    gym?: {
      id: number;
      gym: string;
    };
  };
  // ... otras propiedades de UserData
}

export const AssignedPage = () => {
  const [branchId, setBranchId] = useState(null);
  const user = useSelector(selectUser);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    onSubmit: () => {},
  });
  const { gym } = formik.values;

  const [gyms, { data }] = useFetchGymsMutation();
  const {
    data: branchesByGym,
    isLoading: isLoadingBranches,
    isFetching: isFetchingBranches,
  } = useBranchsGymQuery(
    {
      branchId: gym?.id || user?.branch?.gymId,
    },
    {
      skip: !gym?.id || !user?.branch?.gymId,
    }
  );

  useEffect(() => {
    gyms({
      limit: 1000,
    }).unwrap();
  }, []);

  const supportStaffQuery = useSupportStaffQuery(
    {
      id: user?.id,
    },
    {
      skip: !user?.id,
    }
  );

  const { data: users, isLoading: isLoadingUsers } = useGetAllAssignedQuery(
    {
      branch_id: branchId, // Asegúrate de que este branch_id sea el correcto
    },
    {
      skip: !branchId,
    }
  );

  useEffect(() => {
    if (user?.branch) {
      setBranchId(user.branch.id);
    }
  }, [user]);

  const supportStaff = useMemo(() => {
    if (!supportStaffQuery.data) return [];

    return supportStaffQuery.data;
  }, [supportStaffQuery.data]);

  const gymOptions = useMemo(() => {
    if (!data?.data) return [];

    return data?.data?.map((gym) => ({
      id: gym.id,
      name: gym.gym,
    }));
  }, [data?.data]);

  const optionBranches = useMemo(() => {
    if (!branchesByGym) return [];

    return branchesByGym.map((branch) => ({
      ...branch,
      name: branch.branch,
      id: branch.id,
      gym_id: branch.gym_id,
    }));
  }, [branchesByGym]);

  const peopleToDisplay = useMemo(() => {
    if (!users) return [];

    return users?.users;
  }, [users]);

  const handleChangeValue = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  //set the first gym for branchOptions
  useEffect(() => {
    if (!gymOptions.length) return;
    const gym = gymOptions[0];
    formik.setFieldValue("gym", gym);
  }, [gymOptions]);

  return (
    <div className="px-4 mt-6 md:mt-8">
      <TableHeaderRecovery
        formikValues={formik.values}
        branchOptions={optionBranches}
        companyOptions={gymOptions}
        supportStaff={supportStaff}
        lastVisitOptions={latestVisitOptions}
        userGymId={user?.branch?.gymId}
        handleChangeValue={handleChangeValue}
        handleClickPrimaryButton={() => navigate("/tickets")}
        handleClickSecondaryButton={() => navigate("/tasks")}
        primary_text_button="Incidencias"
        secondary_text_button="Tareas"
        isLoadingBranches={
          isLoadingBranches || isFetchingBranches || optionBranches.length === 0
        }
      />

      <TableListAssigned
        openIncidence={(tike: any) => {
          setOpen(true);
        }}
        people={peopleToDisplay}
        loader={isLoadingUsers}
        support={supportStaff}
      />

      <IncidenceModal
        open={open}
        setOpen={setOpen}
        data={{}}
        branchId={17}
        usersSupportStaff={supportStaff}
      />
    </div>
  );
};

interface TableHeaderRecoveryProps {
  formikValues: any;
  secondary_text_button: string;
  primary_text_button: string;
  companyOptions: Array<GeneralOption>;
  branchOptions: Array<GeneralOption>;
  supportStaff: Array<any>;
  lastVisitOptions: LatestVisitOptionsProp[];
  isLoadingBranches?: boolean;
  userGymId?: string;
  handleClickPrimaryButton: () => void;
  handleClickSecondaryButton: () => void;
  handleChangeValue: (field: string, value: any) => void;
}

const TableHeaderRecovery = ({
  formikValues,
  secondary_text_button,
  primary_text_button,
  branchOptions,
  companyOptions,
  isLoadingBranches,
  supportStaff,
  userGymId,
  handleClickPrimaryButton,
  handleClickSecondaryButton,
  handleChangeValue,
}: TableHeaderRecoveryProps) => {
  const {
    search,
    gym: selectedGym,
    branch: selectedBranch,
    personal: selectedPersonal,
    lastVisit: selectedLastVisit,
  } = formikValues;

  return (
    <Fragment>
      <div>
        <div className="w-full flex-col flex flex-1 justify-between lg:flex-row gap-2">
          <div className="grid flex-1 grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 w-full">
            <InputWithCornerHint
              label="Email"
              span=""
              id="search"
              name="search"
              type="text"
              className="w-full"
              placeholder="email"
              value={search}
              onChange={(event) =>
                handleChangeValue("search", event.target.value)
              }
            />

            <Select
              selected={companyOptions.find((gym) => {
                if (selectedGym?.id) return gym.id === selectedGym?.id;
                else return gym.id === userGymId;
              })}
              setSelected={(gym) => {
                handleChangeValue("gym", gym);
              }}
              options={companyOptions}
              tittle="Compañía"
            />

            <Select
              selected={branchOptions.find(
                (gym) => gym.id === selectedBranch.id
              )}
              setSelected={(branch) => {
                handleChangeValue("branch", branch);
              }}
              options={branchOptions}
              tittle="Filial"
              disabled={isLoadingBranches}
            />
            <Select
              selected={supportStaff.find(
                (gym) => gym.id === selectedPersonal.id
              )}
              setSelected={(branch) => {
                handleChangeValue("personal", branch);
              }}
              options={supportStaff}
              tittle="Personal"
            />
            <Select
              selected={latestVisitOptions.find(
                (lastVisit) => lastVisit.id === selectedLastVisit.id
              )}
              setSelected={(branch) => {
                handleChangeValue("lastVisit", branch);
              }}
              options={latestVisitOptions}
              tittle="Última visita"
            />
          </div>
          <div className="flex items-center justify-end mt-4 gap-2">
            <div className="flex gap-2">
              {primary_text_button ? (
                <ButtonPrimary
                  size="large"
                  text={primary_text_button}
                  onClick={handleClickPrimaryButton}
                />
              ) : null}

              {secondary_text_button ? (
                <ButtonPrimary
                  size="large"
                  text={secondary_text_button}
                  onClick={handleClickSecondaryButton}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
